class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated: the overlay is re-rendered when the cart changes (e.g. after it is cleared), so a listener on it gets lost
    this.addEventListener('click', (evt) => {
      const shouldClose =
        !!evt.target.closest('#CartDrawer-Overlay') || evt.target === this || evt.target.id === 'CartDrawer';
      const inner = this.querySelector('.drawer__inner');
      const innerRect = inner && inner.getBoundingClientRect();
      console.log('[cart-debug] drawer click', {
        target: `${evt.target.tagName.toLowerCase()}${evt.target.id ? '#' + evt.target.id : ''}.${String(evt.target.className).trim().replace(/\s+/g, '.')}`,
        x: evt.clientX,
        y: evt.clientY,
        willClose: shouldClose,
        drawerClasses: this.className,
        hasOverlay: !!this.querySelector('#CartDrawer-Overlay'),
        hasInner: !!inner,
        innerLeft: innerRect ? Math.round(innerRect.left) : null,
        innerWidth: innerRect ? Math.round(innerRect.width) : null,
        viewportWidth: window.innerWidth,
      });
      if (shouldClose) this.close();
    });
    this.watchDrawerMarkup();
    CartDrawer.logCartRequests();
    console.log('[cart-debug] cart-drawer constructed', { classes: this.className, hasOverlay: !!this.querySelector('#CartDrawer-Overlay') });
    this.setHeaderCartIconAccessibility();

    // Back from checkout can restore a stale page (bfcache / HTTP cache), so re-sync the cart
    window.addEventListener('pageshow', (event) => {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (event.persisted || navigation?.type === 'back_forward') this.refreshFromServer();
    });
  }

  // Debug + safety net: something outside the theme (an app) can rewrite the drawer after it opens.
  // Log every change to the drawer (markup and the active class) and put the markup back if it ends up open but empty.
  watchDrawerMarkup() {
    const t0 = performance.now();
    const stamp = () => Math.round(performance.now() - t0) + 'ms';
    const describe = (n) => n.nodeName.toLowerCase() + (n.id ? '#' + n.id : '');
    this.lastGoodMarkup = this.querySelector('#CartDrawer')?.innerHTML || '';

    this.markupObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === 'attributes') {
          if (m.target !== this) return; // descendants' class changes are not interesting
          console.log(`[cart-debug] ${stamp()} <cart-drawer> class: "${m.oldValue}" -> "${this.className}"`);
        } else if (m.addedNodes.length || m.removedNodes.length) {
          console.log(`[cart-debug] ${stamp()} children changed in ${describe(m.target)}`, {
            added: [...m.addedNodes].map(describe),
            removed: [...m.removedNodes].map(describe),
          });
        }
      });

      const target = this.querySelector('#CartDrawer');
      if (!target) {
        console.warn(`[cart-debug] ${stamp()} #CartDrawer is gone from <cart-drawer>`);
        return;
      }
      if (this.reloadingDrawer) return;
      const emptied = !target.querySelector('.drawer__inner') && !target.querySelector('.drawer__inner-empty');
      if (!emptied) {
        this.lastGoodMarkup = target.innerHTML;
        return;
      }
      if (!this.classList.contains('active')) return;
      console.warn(`[cart-debug] ${stamp()} drawer is open but empty -> restoring last good markup`);
      this.reloadingDrawer = true;
      // observer callbacks run before the browser paints, so the empty drawer is never shown
      if (this.lastGoodMarkup) target.innerHTML = this.lastGoodMarkup;
      // then bring in the fresh cart (the app may have just changed it), only touching the DOM if it differs
      this.reloadDrawer().finally(() => {
        this.reloadingDrawer = false;
      });
    });
    this.markupObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  // Debug: log every request to the cart endpoints (theme or app) so the order of events is visible
  static logCartRequests() {
    if (window.__cartDebugFetch) return;
    window.__cartDebugFetch = true;
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        if (url && /\/cart(\/|\.|\?|$)|sections=|section_id=/.test(url)) {
          console.log('[cart-debug] fetch', (init && init.method) || 'GET', url);
        }
      } catch (e) {}
      return originalFetch.apply(this, arguments);
    };
  }

  async refreshFromServer() {
    console.log('[cart-debug] drawer refreshFromServer() (pageshow / back-forward)');
    try {
      const sections = this.getSectionsToRender();
      const response = await fetch(`${window.location.pathname}?sections=${sections.map((s) => s.id).join(',')}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;
      const data = await response.json();
      sections.forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement || !data[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(data[section.id], section.selector);
      });
      this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    } catch (e) {
      console.error(e);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    console.log('[cart-debug] drawer open()', { alreadyActive: this.classList.contains('active'), trigger: triggeredBy?.id || triggeredBy?.tagName });
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // go by what is actually rendered, not the is-empty class, which can be out of step with the markup
        const containerToTrapFocusOn = this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    console.log('[cart-debug] drawer close()', new Error().stack.split('\n').slice(2, 4).join(' | '));
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    console.log('[cart-debug] drawer renderContents()', {
      sections: Object.keys(parsedState.sections || {}),
      drawerSectionLength: parsedState.sections?.['cart-drawer']?.length,
    });
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    // The section response can come back without the drawer markup (e.g. rewritten by a cart app):
    // fetch the drawer fresh so it never opens empty
    console.log('[cart-debug] drawer after render', { hasInner: !!this.querySelector('.drawer__inner'), hasOverlay: !!this.querySelector('#CartDrawer-Overlay') });
    const ready = this.querySelector('.drawer__inner') ? Promise.resolve() : this.reloadDrawer();
    ready.then(() => {
      setTimeout(() => this.open());
    });
  }

  async reloadDrawer() {
    console.warn('[cart-debug] drawer markup missing, reloading cart-drawer section');
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' });
      if (!response.ok) return;
      const html = await response.text();
      const fresh = this.getSectionDOM(html, '#CartDrawer');
      const current = document.getElementById('CartDrawer');
      if (fresh && current && current.innerHTML !== fresh.innerHTML) current.innerHTML = fresh.innerHTML;
      if (current) this.lastGoodMarkup = current.innerHTML;
      this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    } catch (e) {
      console.error(e);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-switch-subscription]');
      if (!button || button.disabled) return;
      this.switchToSubscription(button);
    });
  }

  switchToSubscription(button) {
    const line = Number(button.dataset.line);
    const sellingPlan = Number(button.dataset.sellingPlan);
    if (!line || !sellingPlan) return;

    button.disabled = true;
    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const body = JSON.stringify({
      line,
      quantity: Number(this.querySelector(`#Drawer-quantity-${line}`)?.value) || 1,
      selling_plan: sellingPlan,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          this.dispatchCartErrorEvent(parsedState.errors, 'INVALID');
          button.disabled = false;
          return;
        }

        sectionsToRender.forEach((section) => {
          const container = document.getElementById(section.id);
          const target = container?.querySelector(section.selector) || container;
          if (!target) return;
          target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch(() => {
        button.disabled = false;
        this.dispatchCartErrorEvent(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
